package com.ijoin.ui.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Image
import androidx.compose.material.icons.outlined.LocationOn
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import com.ijoin.data.api.ApiClient
import com.ijoin.ui.util.toThumbUrl
import com.ijoin.data.model.Event
import com.ijoin.platform.createTimeZone
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAccentSoft
import com.ijoin.ui.design.EventCardBadgeBackground
import com.ijoin.ui.design.EventCardBadgeText
import com.ijoin.ui.design.EventCardCorner
import com.ijoin.ui.design.EventCardDateBadgeBackground
import com.ijoin.ui.design.EventCardDateBadgeText
import com.ijoin.ui.design.EventCardOutline
import com.ijoin.ui.design.EventCardPriceFree
import com.ijoin.ui.design.EventCardPricePaid
import com.ijoin.ui.design.EventCardSurface
import com.ijoin.ui.design.EventPill
import com.ijoin.ui.design.EventPrimaryButton
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

@Composable
fun RecommendedEventCard(
    event: Event,
    onEventClick: (String) -> Unit,
    onJoinClick: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    Card(
        modifier = modifier
            .fillMaxWidth()
            .clickable { onEventClick(event.id) },
        shape = RoundedCornerShape(EventCardCorner),
        colors = CardDefaults.cardColors(containerColor = EventCardSurface),
        border = BorderStroke(1.dp, EventCardOutline),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column {
            // Image with badge and date overlay
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(390.dp)
            ) {
                // Event image
                if (!event.mediaUrls.isNullOrEmpty()) {
                    val first = event.mediaUrls.first()
                    val thumbPath = if (first.startsWith("http")) first else toThumbUrl(first, 1080)
                    val imageUrl = if (thumbPath.startsWith("http")) thumbPath else "${ApiClient.getBaseUrl()}$thumbPath"
                    AsyncImage(
                        model = imageUrl,
                        contentDescription = event.title,
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Crop
                    )
                } else {
                    // Placeholder
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .background(EventCardAccentSoft),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            imageVector = Icons.Outlined.Image,
                            contentDescription = null,
                            modifier = Modifier.size(64.dp),
                            tint = EventCardAccentDark.copy(alpha = 0.4f)
                        )
                    }
                }

                // Category badge (bottom left)
                event.category?.let { category ->
                    EventPill(
                        text = category.uppercase(),
                        backgroundColor = EventCardBadgeBackground,
                        textColor = EventCardBadgeText,
                        modifier = Modifier
                            .align(Alignment.BottomStart)
                            .padding(12.dp)
                    )
                }

                // Date (top right)
                val badgeText = formatEventDateShort(event.eventDate, event.eventEndDate, event.timeZone)
                if (badgeText != null) {
                    EventPill(
                        text = badgeText,
                        backgroundColor = EventCardDateBadgeBackground,
                        textColor = EventCardDateBadgeText,
                        borderColor = EventCardOutline,
                        textStyle = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier
                            .align(Alignment.TopEnd)
                            .padding(12.dp)
                    )
                }
            }

            // Event details
            Column(
                modifier = Modifier.padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                // Event title
                Text(
                    text = event.title,
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )

                // Description
                event.description?.let { description ->
                    Text(
                        text = description,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis
                    )
                }

                // Location and price row
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Location
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.weight(1f)
                    ) {
                        Icon(
                            imageVector = Icons.Outlined.LocationOn,
                            contentDescription = null,
                            modifier = Modifier.size(18.dp),
                            tint = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Spacer(modifier = Modifier.width(4.dp))
                        Text(
                            text = event.locationCity ?: event.locationAddress ?: "Online",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }

                    // Price
                    event.priceTiers?.minByOrNull { it.price }?.let { minTier ->
                        Text(
                            text = formatPrice(minTier.price),
                            style = MaterialTheme.typography.headlineSmall,
                            fontWeight = FontWeight.Bold,
                            color = if (minTier.price == 0.0) EventCardPriceFree else EventCardPricePaid
                        )
                    }
                }

                // Join button
                EventPrimaryButton(
                    text = "Join ${event.category ?: "Event"}",
                    onClick = { onJoinClick(event.id) },
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
}

private fun formatEventDateShort(start: Instant?, end: Instant?, timeZone: String? = null): String? {
    val startValue = start ?: return null
    val zone = if (!timeZone.isNullOrBlank()) createTimeZone(timeZone) else TimeZone.UTC
    val startDate = startValue.toLocalDateTime(zone).date
    val startLabel = toMonthDay(startDate)
    val endDate = end?.toLocalDateTime(zone)?.date
    val endLabel = when {
        endDate == null -> null
        endDate == startDate -> null
        else -> toMonthDay(endDate)
    }
    return if (endLabel == null) startLabel else "$startLabel – $endLabel"
}

private fun toMonthDay(date: kotlinx.datetime.LocalDate): String {
    return try {
        val month = getMonthAbbr(date.monthNumber)
        val day = date.dayOfMonth
        "$month $day"
    } catch (e: Exception) {
        date.toString()
    }
}

private fun formatPrice(price: Double): String {
    return if (price == 0.0) "Free" else "$$${price.toInt()}"
}

private fun getMonthAbbr(month: Int): String {
    return when (month) {
        1 -> "Jan"
        2 -> "Feb"
        3 -> "Mar"
        4 -> "Apr"
        5 -> "May"
        6 -> "Jun"
        7 -> "Jul"
        8 -> "Aug"
        9 -> "Sep"
        10 -> "Oct"
        11 -> "Nov"
        12 -> "Dec"
        else -> ""
    }
}
